import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { annonceService } from '../../../api';
import Sidebar from '../sidebar/Sidebar';
import { FaEdit, FaTrash, FaEye, FaInfoCircle } from 'react-icons/fa';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import './annonces.css';

const Annonces = () => {
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    type: '',
    ville: '',
    sous_service: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchAnnonces();
  }, [currentPage, filters]);

  const fetchAnnonces = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        page: currentPage,
        ...(filters.type && { type: filters.type }),
        ...(filters.ville && { ville: filters.ville }),
        ...(filters.sous_service && { sous_service: filters.sous_service })
      };

      const response = await annonceService.getAll(params);
      const annoncesData = response.data?.data || response.data || [];
      setAnnonces(annoncesData);
      
      setTotalPages(response.data?.meta?.last_page || 
                   response.meta?.last_page || 
                   1);
    } catch (error) {
      console.error("Erreur lors du chargement des annonces:", error);
      setError(error.message || "Erreur lors du chargement des annonces");
      setAnnonces([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    confirmAlert({
      title: 'Confirmer la suppression',
      message: 'Êtes-vous sûr de vouloir supprimer cette annonce ?',
      buttons: [
        {
          label: 'Oui',
          onClick: async () => {
            try {
              setLoading(true);
              setError(null);
              
              const result = await annonceService.delete(id);
              
              if (result?.success) {
                setSuccess(result.message || 'Annonce supprimée avec succès');
                setAnnonces(prev => prev.filter(annonce => annonce.id !== id));
                
                // Si c'était le dernier élément de la page, revenir à la page précédente
                if (annonces.length === 1 && currentPage > 1) {
                  setCurrentPage(currentPage - 1);
                }
              } else {
                setError(result?.message || 'Erreur lors de la suppression');
              }
            } catch (err) {
              console.error('Erreur détaillée:', {
                message: err.message,
                status: err.status,
                data: err.data
              });
              
              setError(err.message || 'Erreur lors de la suppression');
            } finally {
              setLoading(false);
              setTimeout(() => {
                setSuccess(null);
                setError(null);
              }, 5000);
            }
          }
        },
        {
          label: 'Non'
        }
      ]
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  return (
    <div className="admin-layout">
      <Sidebar />
      
      <div className="admin-content">
        <div className="annonces-header">
          <h1>Gestion des annonces</h1>
          <Link to="/admin/annonces/creer" className="btn-primary">
            + Créer une annonce
          </Link>
        </div>

        {/* Messages d'erreur/succès */}
        {error && (
          <div className="alert alert-danger">
            {error}
            <button onClick={() => setError(null)} className="close-btn">×</button>
          </div>
        )}
        
        {success && (
          <div className="alert alert-success">
            {success}
            <button onClick={() => setSuccess(null)} className="close-btn">×</button>
          </div>
        )}

        {/* Filtres */}
        <div className="annonces-filters">
          <select 
            name="type" 
            onChange={handleFilterChange}
            value={filters.type}
          >
            <option value="">Tous les types</option>
            <option value="offre">Offres</option>
            <option value="demande">Demandes</option>
          </select>

          <input
            type="text"
            name="ville"
            placeholder="Filtrer par ville"
            onChange={handleFilterChange}
            value={filters.ville}
          />

          <input
            type="text"
            name="sous_service"
            placeholder="Filtrer par sous-service"
            onChange={handleFilterChange}
            value={filters.sous_service}
          />
        </div>

        {/* Tableau des annonces */}
        {loading ? (
          <div className="loading">Chargement...</div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="annonces-table">
                <thead>
                  <tr>
                    <th>Titre</th>
                    <th>Type</th>
                    <th>Ville</th>
                    <th>Prix (€)</th>
                    <th className="description-column">
                      Description <FaInfoCircle className="info-icon" />
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {annonces.length > 0 ? (
                    annonces.map(annonce => (
                      <tr key={annonce.id}>
                        <td>{annonce.titre}</td>
                        <td>
                          <span className={`badge ${annonce.type_annonce}`}>
                            {annonce.type_annonce === 'offre' ? 'Offre' : 'Demande'}
                          </span>
                        </td>
                        <td>{annonce.ville}</td>
                        <td>{annonce.prix ? `${annonce.prix} €` : 'Non spécifié'}</td>
                        <td className="description-cell">
                          <div className="description-content">
                            {annonce.description?.length > 100 
                              ? `${annonce.description.substring(0, 100)}...` 
                              : annonce.description}
                          </div>
                        </td>
                        <td className="actions-cell">
                          <Link 
                            to={`/admin/annonces/${annonce.id}`} 
                            className="btn-icon"
                            title="Voir"
                          >
                            <FaEye />
                          </Link>
                          <Link 
                            to={`/admin/annonces/${annonce.id}/edit`} 
                            className="btn-icon"
                            title="Modifier"
                          >
                            <FaEdit />
                          </Link>
                          <button 
                            className="btn-icon danger"
                            title="Supprimer"
                            onClick={() => handleDelete(annonce.id)}
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        Aucune annonce trouvée
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Précédent
                </button>
                
                <span>Page {currentPage} sur {totalPages}</span>
                
                <button 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Annonces;